import Toaster from '@meforma/vue-toaster';
import * as Sentry from '@sentry/vue';
import { createPinia } from 'pinia';
import { createApp } from 'vue';

import App from './App.vue';
import router, { RouteMeta, RouteTypes } from './router/router';
import './index.css';
import { useCurrentAdminStore } from './stores/admin';
import { useCurrentDoctorStore } from './stores/doctor';
import { useCurrentPatientStore } from './stores/patient';

const app = createApp(App);
app.use(router);
app.use(createPinia());
app.use(Toaster).provide('toast', app.config.globalProperties.$toast);

const getSentryEnv = () => {
  const domain = document.domain;
  switch (domain) {
    case 'dev.doctorsrecipes.jp':
      return 'develop';
    case 'stg.doctorsrecipes.jp':
      return 'staging';
    case 'app.doctorsrecipes.jp':
      return 'production';
    default:
      return 'local';
  }
};
const sentryEnv = getSentryEnv();

Sentry.init({
  app,
  dsn: 'https://e185cd205b6eca00bf4d504645457a20@o4506982219317248.ingest.us.sentry.io/4506982221479936',
  environment: sentryEnv,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/(stg|app|dev)\.doctorsrecipes\.jp\/$/,
    /^https:\/\/(stg-api|api|dev-api)\.doctorsrecipes\.jp\/$/,
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});
app.mount('#app');

/**
 * ログイン時に認証ページへアクセスした場合と、未ログイン時に未認証ページへアクセスした場合は遷移を許可する
 */
const authorize = (isLoggedIn: boolean, meta: RouteMeta) => {
  return (
    (isLoggedIn && meta.requiredAuth) || (!isLoggedIn && !meta.requiredAuth)
  );
};

router.beforeEach(async (to, _, next) => {
  const meta = to.meta as RouteMeta;

  let isAuthorized = false;
  if (meta.type === RouteTypes.DOCTOR) {
    const doctorStore = useCurrentDoctorStore();
    isAuthorized = authorize(await doctorStore.isLoggedIn(), meta);
  }

  if (meta.type === RouteTypes.PATIENT) {
    const patientStore = useCurrentPatientStore();
    isAuthorized = authorize(await patientStore.isLoggedIn(), meta);
  }

  if (meta.type === RouteTypes.ADMIN) {
    const adminStore = useCurrentAdminStore();
    isAuthorized = authorize(await adminStore.isLoggedIn(), meta);
  }

  // アクセスが許可されていない場合はリダイレクトする
  if (!isAuthorized) {
    next(meta.redirectTo);
    return;
  }

  next();
});

export default router;
